import React, { useState, useEffect } from 'react';
import {
  Button, Table, Tag, Modal, Form, Input, Select,
  Typography, Space, Alert, Tooltip, Spin, Popconfirm, message,
  Row, Col, Empty,
} from 'antd';
import {
  ApiOutlined, PlusOutlined, DeleteOutlined, EditOutlined,
  PlayCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  LinkOutlined, ThunderboltOutlined, LockOutlined, CrownOutlined,
  SyncOutlined, FilterOutlined, CodeOutlined, RocketOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PRO_PRICE_MO } from '../utils/pricing';

const { Text } = Typography;
const { Option } = Select;

// ── Decode JWT ──────────────────────────────────────────────────────────────
const decodeToken = () => {
  try {
    const t = localStorage.getItem('token');
    if (!t) return {};
    const p = t.split('.')[1];
    const padded = p + '='.repeat((4 - p.length % 4) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
};

// ── Field map fields ────────────────────────────────────────────────────────
const FIELD_MAP_FIELDS = [
  { key: 'email',      label: 'Email',      required: true },
  { key: 'first_name', label: 'First Name'  },
  { key: 'last_name',  label: 'Last Name'   },
  { key: 'name',       label: 'Full Name'   },
  { key: 'phone',      label: 'Phone'       },
  { key: 'city',       label: 'City'        },
  { key: 'state',      label: 'State'       },
  { key: 'country',    label: 'Country'     },
];

const AUTH_TYPE_LABELS = {
  none:    'None',
  bearer:  'Bearer Token',
  api_key: 'API Key',
  basic:   'Basic Auth',
};

const AUTH_TYPE_COLORS = {
  none:    'default',
  bearer:  'blue',
  api_key: 'purple',
  basic:   'orange',
};

// ── Main component ──────────────────────────────────────────────────────────
const DynamicLists = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ── State ────────────────────────────────────────────────────────────────
  const [lists,      setLists]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  const [modalOpen,  setModalOpen]  = useState(false);
  const [modalMode,  setModalMode]  = useState('create'); // 'create' | 'edit'
  const [editingId,  setEditingId]  = useState(null);

  const [form] = Form.useForm();
  const authType = Form.useWatch('auth_type', form);
  const method   = Form.useWatch('method', form);

  const [testResult, setTestResult] = useState(null);
  const [testError,  setTestError]  = useState(null);
  const [testing,    setTesting]    = useState(false);
  const [saving,     setSaving]     = useState(false);

  const payload = decodeToken();
  const isPro   = payload?.plan === 'pro';

  // ── Load lists ────────────────────────────────────────────────────────────
  const loadLists = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/dynamic-lists', { headers });
      setLists(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load dynamic lists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPro) loadLists();
    else setLoading(false);
  }, []);

  // ── Open modal ────────────────────────────────────────────────────────────
  const openCreate = () => {
    setModalMode('create');
    setEditingId(null);
    setTestResult(null);
    setTestError(null);
    form.resetFields();
    form.setFieldsValue({ method: 'GET', auth_type: 'none' });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setModalMode('edit');
    setEditingId(record.id);
    setTestResult(null);
    setTestError(null);

    const cfg = record.config || {};
    let field_map = {};
    if (cfg.field_map) {
      try {
        field_map = typeof cfg.field_map === 'string' ? JSON.parse(cfg.field_map) : cfg.field_map;
      } catch {}
    }

    form.setFieldsValue({
      name:          record.name,
      description:   record.description || '',
      api_url:       cfg.api_url || '',
      method:        cfg.method  || 'GET',
      auth_type:     cfg.auth_type || 'none',
      auth_header:   cfg.auth_header || '',
      auth_value:    cfg.auth_value || '',
      request_body:  cfg.request_body || '',
      response_path: cfg.response_path || '',
      field_map,
    });

    setModalOpen(true);
  };

  // ── Test from modal ───────────────────────────────────────────────────────
  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setTestError(null);

    try {
      const values = form.getFieldsValue();
      const field_map = values.field_map || {};

      const body = {
        api_url:       values.api_url,
        method:        values.method || 'GET',
        auth_type:     values.auth_type || 'none',
        auth_header:   values.auth_header || null,
        auth_value:    values.auth_value || null,
        request_body:  values.request_body || null,
        response_path: values.response_path || null,
        field_map:     Object.keys(field_map).length > 0 ? field_map : null,
      };

      if (modalMode === 'edit' && body.auth_value === '••••••••') {
        const res = await axios.post(`/api/dynamic-lists/${editingId}/test`, {}, { headers });
        setTestResult(res.data);
      } else {
        const res = await axios.post('/api/dynamic-lists/test', body, { headers });
        setTestResult(res.data);
      }
    } catch (err) {
      setTestError(err.response?.data?.message || err.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    setSaving(true);
    try {
      const values = form.getFieldsValue();
      const field_map = values.field_map || {};

      const body = {
        name:          values.name,
        description:   values.description || null,
        api_url:       values.api_url,
        method:        values.method || 'GET',
        auth_type:     values.auth_type || 'none',
        auth_header:   values.auth_header || null,
        auth_value:    values.auth_value || null,
        request_body:  values.request_body || null,
        response_path: values.response_path || null,
        field_map:     Object.keys(field_map).length > 0 ? field_map : null,
      };

      if (modalMode === 'create') {
        const res = await axios.post('/api/dynamic-lists', body, { headers });
        setLists(prev => [res.data, ...prev]);
        message.success('Dynamic list created');
      } else {
        const res = await axios.put(`/api/dynamic-lists/${editingId}`, body, { headers });
        setLists(prev => prev.map(l => l.id === editingId ? res.data : l));
        message.success('Dynamic list updated');
      }

      setModalOpen(false);
    } catch (err) {
      message.error(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/dynamic-lists/${id}`, { headers });
      setLists(prev => prev.filter(l => l.id !== id));
      message.success('Dynamic list deleted');
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  // ── Test existing (from table) ────────────────────────────────────────────
  const handleTestExisting = async (id) => {
    try {
      const res = await axios.post(`/api/dynamic-lists/${id}/test`, {}, { headers });
      setLists(prev => prev.map(l => {
        if (l.id !== id) return l;
        return {
          ...l,
          config: l.config ? {
            ...l.config,
            last_fetched_at: new Date().toISOString(),
            last_count: res.data.total,
          } : l.config,
        };
      }));
      message.success(`Fetched ${res.data.total} subscribers`);
    } catch (err) {
      message.error(err.response?.data?.message || 'Test failed');
    }
  };

  // ── Pro gate ──────────────────────────────────────────────────────────────
  if (!isPro) {
    const FEATURES = [
      {
        icon: <SyncOutlined style={{ fontSize: 24, color: 'var(--brand-primary)' }} />,
        title: 'Always Fresh Audiences',
        desc: 'Connect any REST API — your CRM, e-commerce platform, or database — and subscribers are pulled live at send time.',
      },
      {
        icon: <FilterOutlined style={{ fontSize: 24, color: 'var(--ok)' }} />,
        title: 'Smart Segmentation',
        desc: 'Target active customers, trial users, churned accounts — any slice your API can return.',
      },
      {
        icon: <CodeOutlined style={{ fontSize: 24, color: 'var(--warn)' }} />,
        title: 'Works With Any API',
        desc: 'Supports GET & POST, Bearer tokens, API keys, and Basic auth. Map any JSON field in seconds.',
      },
      {
        icon: <RocketOutlined style={{ fontSize: 24, color: 'var(--danger)' }} />,
        title: 'Zero Manual Work',
        desc: 'Set it up once — every campaign send automatically fetches the latest list.',
      },
    ];

    const PREVIEW_ROWS = [
      { name: 'Active Customers',  url: 'https://api.example.com/customers?status=active', last_sync: '2 min ago', count: 1842  },
      { name: 'Trial Users',       url: 'https://api.myapp.io/users?plan=trial',           last_sync: '5 min ago', count: 347   },
      { name: 'Churned — 30 days', url: 'https://crm.company.com/segments/churned',        last_sync: '11 min ago', count: 93   },
    ];

    return (
      <div style={{ maxWidth: 860, margin: '0 auto' }}>

        {/* Hero */}
        <div style={{
          textAlign: 'center',
          padding: '48px 24px 36px',
          background: 'var(--surface)',
          borderRadius: 'var(--r-lg)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-sm)',
          marginBottom: 20,
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'var(--brand-tint)', border: '1px solid color-mix(in srgb, var(--brand-primary) 25%, transparent)',
            borderRadius: 20, padding: '3px 12px', marginBottom: 16,
          }}>
            <CrownOutlined style={{ color: 'var(--brand-primary)', fontSize: 12 }} />
            <span style={{ color: 'var(--brand-primary)', fontSize: 12, fontWeight: 600 }}>Pro Feature</span>
          </div>

          <div style={{ fontSize: 26, fontWeight: 750, letterSpacing: '-0.03em', color: 'var(--text)', marginBottom: 10 }}>
            Dynamic List
          </div>
          <Text style={{ fontSize: 15, color: 'var(--text-3)', display: 'block', maxWidth: 500, margin: '0 auto 28px', lineHeight: 1.6 }}>
            Connect your APIs and send to live, always-updated audiences — no manual CSV exports ever again.
          </Text>

          <Button
            type="primary"
            size="large"
            icon={<CrownOutlined />}
            onClick={() => navigate('/billing')}
            style={{ borderRadius: 'var(--r-md)', fontWeight: 600, height: 44, paddingInline: 28 }}
          >
            Upgrade to Pro — {PRO_PRICE_MO}
          </Button>
          <div style={{ marginTop: 10 }}>
            <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>
              Unlimited campaigns · Unlimited contacts · Dynamic List included
            </Text>
          </div>
        </div>

        {/* Feature cards */}
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
          {FEATURES.map(f => (
            <Col xs={24} sm={12} key={f.title}>
              <div style={{
                borderRadius: 'var(--r-lg)', border: '1px solid var(--border)',
                background: 'var(--surface)', padding: '18px 20px',
                boxShadow: 'var(--shadow-sm)', height: '100%',
              }}>
                <div style={{ marginBottom: 10 }}>{f.icon}</div>
                <Text strong style={{ fontSize: 13.5, display: 'block', marginBottom: 5, color: 'var(--text)' }}>{f.title}</Text>
                <Text style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.6 }}>{f.desc}</Text>
              </div>
            </Col>
          ))}
        </Row>

        {/* Blurred preview table */}
        <div style={{ position: 'relative' }}>
          <div style={{
            borderRadius: 'var(--r-lg)', overflow: 'hidden',
            border: '1px solid var(--border)',
            filter: 'blur(2px)',
            userSelect: 'none',
            pointerEvents: 'none',
            opacity: 0.55,
          }}>
            <div style={{
              background: 'var(--surface)',
              padding: '12px 20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <Text strong style={{ color: 'var(--text)' }}>Your Dynamic Lists</Text>
              <Tag>3 connected</Tag>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'var(--surface)' }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['Name', 'API Endpoint', 'Last Synced', 'Subscribers'].map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left', fontWeight: 600,
                      color: 'var(--text-3)', borderBottom: '1px solid var(--border)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PREVIEW_ROWS.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text)' }}>{r.name}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--brand-primary)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.url}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--text-3)' }}>{r.last_sync}</td>
                    <td style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--ok)' }}>{r.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Lock overlay */}
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.6)',
            borderRadius: 'var(--r-lg)',
            backdropFilter: 'blur(1px)',
            gap: 12,
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: 'var(--r-lg)',
              background: 'var(--brand-tint)', border: '1px solid color-mix(in srgb, var(--brand-primary) 25%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <LockOutlined style={{ fontSize: 20, color: 'var(--brand-primary)' }} />
            </div>
            <Text strong style={{ fontSize: 14, color: 'var(--text)' }}>Unlock Dynamic List with Pro</Text>
            <Button type="primary" icon={<CrownOutlined />} onClick={() => navigate('/billing')} style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}>
              Upgrade Now
            </Button>
          </div>
        </div>

      </div>
    );
  }

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => (
        <Space size={10}>
          <div style={{
            width: 34, height: 34, background: 'var(--brand-tint)',
            borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <ApiOutlined style={{ color: 'var(--brand-primary)', fontSize: 15 }} />
          </div>
          <div>
            <Text strong style={{ fontSize: 13 }}>{name}</Text>
            <div>
              <Tag icon={<ThunderboltOutlined />} style={{ fontSize: 11, borderRadius: 20, marginTop: 2 }}>Dynamic</Tag>
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'API Endpoint',
      key: 'api_url',
      render: (_, record) => {
        const url = record.config?.api_url || '';
        const truncated = url.length > 48 ? url.slice(0, 48) + '…' : url;
        return (
          <Tooltip title={url}>
            <Text code style={{ fontSize: 11, color: 'var(--text-2)' }}>{truncated || '—'}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Auth',
      key: 'auth_type',
      width: 120,
      render: (_, record) => {
        const t = record.config?.auth_type || 'none';
        return (
          <Tag color={AUTH_TYPE_COLORS[t] || 'default'} style={{ borderRadius: 20 }}>
            {AUTH_TYPE_LABELS[t] || t}
          </Tag>
        );
      },
    },
    {
      title: 'Last Count',
      key: 'last_count',
      width: 160,
      render: (_, record) => {
        const count = record.config?.last_count;
        if (!count && count !== 0) return <Text style={{ color: 'var(--text-3)' }}>—</Text>;
        return (
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
            {count.toLocaleString()}
            <span style={{ fontWeight: 400, color: 'var(--text-3)', fontSize: 12, marginLeft: 4 }}>subscribers</span>
          </span>
        );
      },
    },
    {
      title: 'Last Tested',
      key: 'last_fetched_at',
      width: 140,
      render: (_, record) => {
        const d = record.config?.last_fetched_at;
        if (!d) return <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>Never</Text>;
        return (
          <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </Text>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      align: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Test connection">
            <Button size="small" icon={<PlayCircleOutlined />} style={{ borderRadius: 8 }} onClick={() => handleTestExisting(record.id)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} style={{ borderRadius: 8 }} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="Delete this dynamic list?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 8 }} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Preview table columns ─────────────────────────────────────────────────
  const previewColumns = [
    { title: 'Email',      dataIndex: 'email',      key: 'email',      ellipsis: true },
    { title: 'First Name', dataIndex: 'first_name', key: 'first_name', width: 100 },
    { title: 'Last Name',  dataIndex: 'last_name',  key: 'last_name',  width: 100 },
    { title: 'City',       dataIndex: 'city',       key: 'city',       width: 80  },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Action bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={openCreate}
          style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}
        >
          New Dynamic List
        </Button>
      </div>

      {error && (
        <Alert
          message={error} type="error" showIcon closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }}
        />
      )}

      <div style={{
        borderRadius: 'var(--r-lg)', border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)', background: 'var(--surface)', overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={lists}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 20, showSizeChanger: false, showTotal: t => `${t} list${t !== 1 ? 's' : ''}`, style: { padding: '8px 16px' } }}
            size="middle"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span style={{ color: 'var(--text-3)' }}>
                      No dynamic lists yet. Create your first to pull subscribers from an API.
                    </span>
                  }
                >
                  <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}>
                    New Dynamic List
                  </Button>
                </Empty>
              ),
            }}
            style={{ borderRadius: 0 }}
          />
        )}
      </div>

      {/* ── Create / Edit Modal ── */}
      <Modal
        title={
          <Space>
            <div style={{
              width: 32, height: 32, background: 'var(--brand-tint)',
              borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ApiOutlined style={{ color: 'var(--brand-primary)' }} />
            </div>
            <span>{modalMode === 'create' ? 'Create Dynamic List' : 'Edit Dynamic List'}</span>
          </Space>
        }
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setTestResult(null); setTestError(null); }}
        width={700}
        footer={
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={() => { setModalOpen(false); setTestResult(null); setTestError(null); }}>
              Cancel
            </Button>
            <Button
              icon={<PlayCircleOutlined />}
              loading={testing}
              onClick={handleTest}
            >
              Test Connection
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={saving}
              onClick={handleSave}
              style={{ borderRadius: 'var(--r-md)' }}
            >
              {modalMode === 'create' ? 'Create List' : 'Save Changes'}
            </Button>
          </Space>
        }
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          {/* Basic info */}
          <Row gutter={[12, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name="name" label="List Name" rules={[{ required: true, message: 'Name required' }]}>
                <Input placeholder="e.g., HubSpot Leads" style={{ borderRadius: 'var(--r-md)' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="description" label="Description">
                <Input placeholder="Optional description" style={{ borderRadius: 'var(--r-md)' }} />
              </Form.Item>
            </Col>
          </Row>

          {/* API config */}
          <div style={{
            borderRadius: 'var(--r-md)', border: '1px solid var(--border)',
            background: 'var(--surface-2)', padding: '14px 16px', marginBottom: 12,
          }}>
            <Text strong style={{ fontSize: 13, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
              <LinkOutlined style={{ color: 'var(--brand-primary)' }} /> API Configuration
            </Text>
            <Row gutter={[12, 0]}>
              <Col xs={24} md={16}>
                <Form.Item
                  name="api_url"
                  label="API Endpoint URL"
                  rules={[
                    { required: true, message: 'API URL required' },
                    { type: 'url', message: 'Enter a valid URL with http/https' },
                  ]}
                >
                  <Input placeholder="https://api.yourcrm.com/contacts" style={{ borderRadius: 'var(--r-md)' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="method" label="HTTP Method">
                  <Select style={{ borderRadius: 'var(--r-md)' }}>
                    <Option value="GET">GET</Option>
                    <Option value="POST">POST</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Auth section */}
            <Row gutter={[12, 0]}>
              <Col xs={24} md={8}>
                <Form.Item name="auth_type" label="Authentication">
                  <Select>
                    <Option value="none">None</Option>
                    <Option value="bearer">Bearer Token</Option>
                    <Option value="api_key">API Key Header</Option>
                    <Option value="basic">Basic Auth</Option>
                  </Select>
                </Form.Item>
              </Col>
              {authType === 'bearer' && (
                <Col xs={24} md={16}>
                  <Form.Item name="auth_value" label="Bearer Token">
                    <Input.Password placeholder="your-bearer-token" style={{ borderRadius: 'var(--r-md)' }} />
                  </Form.Item>
                </Col>
              )}
              {authType === 'api_key' && (
                <>
                  <Col xs={24} md={8}>
                    <Form.Item name="auth_header" label="Header Name">
                      <Input placeholder="X-API-Key" style={{ borderRadius: 'var(--r-md)' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="auth_value" label="API Key">
                      <Input.Password placeholder="your-api-key" style={{ borderRadius: 'var(--r-md)' }} />
                    </Form.Item>
                  </Col>
                </>
              )}
              {authType === 'basic' && (
                <Col xs={24} md={16}>
                  <Form.Item name="auth_value" label="Credentials (username:password)">
                    <Input.Password placeholder="username:password" style={{ borderRadius: 'var(--r-md)' }} />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Row gutter={[12, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="response_path"
                  label="Response Path"
                  extra={<span style={{ color: 'var(--text-3)', fontSize: 12 }}>Dot-path to the array e.g. "data.contacts"</span>}
                >
                  <Input placeholder="data.users" style={{ borderRadius: 'var(--r-md)' }} />
                </Form.Item>
              </Col>
            </Row>

            {method === 'POST' && (
              <Form.Item
                name="request_body"
                label="Request Body (JSON)"
                extra={<span style={{ color: 'var(--text-3)', fontSize: 12 }}>JSON body sent with POST requests</span>}
              >
                <Input.TextArea
                  rows={3}
                  placeholder='{"page":1,"limit":1000}'
                  style={{ borderRadius: 'var(--r-md)', fontFamily: 'var(--font-mono)', fontSize: 12 }}
                />
              </Form.Item>
            )}
          </div>

          {/* Field mapping */}
          <div style={{
            borderRadius: 'var(--r-md)', border: '1px solid var(--border)',
            background: 'var(--surface-2)', padding: '14px 16px', marginBottom: 12,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <Text strong style={{ fontSize: 13, color: 'var(--text)' }}>Field Mapping</Text>
              <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>Map API fields → subscriber fields</Text>
            </div>
            <Row gutter={[12, 4]}>
              {FIELD_MAP_FIELDS.map(({ key, label, required }) => (
                <Col xs={24} md={12} key={key}>
                  <Form.Item
                    name={['field_map', key]}
                    label={
                      <span style={{ fontSize: 12 }}>
                        <span style={{ color: 'var(--text)', fontWeight: 600 }}>{label}</span>
                        {required && <span style={{ color: 'var(--danger)', marginLeft: 4 }}>*</span>}
                        <span style={{ color: 'var(--text-3)', marginLeft: 6 }}>←</span>
                      </span>
                    }
                    style={{ marginBottom: 4 }}
                  >
                    <Input
                      placeholder={`API field (default: "${key}")`}
                      size="small"
                      style={{ borderRadius: 6, fontSize: 12 }}
                    />
                  </Form.Item>
                </Col>
              ))}
            </Row>
            <Text style={{ fontSize: 11, color: 'var(--text-3)' }}>
              Leave blank to use the default field name. Fill in if your API uses different names (e.g., "email_address" for email).
            </Text>
          </div>

          {/* Test result */}
          {testResult && (
            <Alert
              type="success"
              icon={<CheckCircleOutlined />}
              showIcon
              message={
                <div>
                  <Text strong>Connection successful — {testResult.total.toLocaleString()} subscribers found</Text>
                  {testResult.preview?.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Table
                        dataSource={testResult.preview}
                        columns={previewColumns}
                        size="small"
                        pagination={false}
                        rowKey="email"
                        style={{ fontSize: 12 }}
                      />
                    </div>
                  )}
                </div>
              }
              style={{ borderRadius: 'var(--r-md)', marginTop: 4 }}
            />
          )}
          {testError && (
            <Alert
              type="error"
              icon={<ExclamationCircleOutlined />}
              showIcon
              message={<Text><strong>Test failed:</strong> {testError}</Text>}
              style={{ borderRadius: 'var(--r-md)', marginTop: 4 }}
            />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default DynamicLists;
